/**
 * DXF Library — typed endpoint helpers.
 *
 * BE surface: ``ando-agent/src/api/v1/endpoints/dxf_library.py``:
 *
 *   GET    /dxf-library/files                   list (category/status/search/limit/offset)
 *   GET    /dxf-library/files/{id}              single row detail
 *   GET    /dxf-library/files/{id}/explore      CAD entity explorer data (layers/blocks/texts)
 *   PATCH  /dxf-library/files/{id}              update category/description
 *   DELETE /dxf-library/files/{id}              soft-delete
 *   GET    /dxf-library/files/{id}/versions     modification history
 *   GET    /dxf-library/categories              list distinct categories + counts
 *   PUT    /dxf-library/categories              rename
 *   GET    /dxf-library/stats                   aggregated dashboard stats
 *
 * Upload reuses the existing chat-attachment / unified upload pipeline
 * (``uploads.ts``) — there is no dedicated ``/dxf-library/upload`` endpoint;
 * the ingestion worker auto-categorises ``.dxf`` / ``.dwg`` MIME types into
 * the library on the BE side.
 *
 * Wire shapes are kept loose (``Record<string, unknown>``) at the boundary
 * because the BE row schema is wide (40+ columns: minio key, presigned URLs,
 * cad_analysis blob, version count, etc.). Feature slices refine via zod
 * where the strict shape matters.
 */

import { apiClient } from "../ApiClient.js";

const BASE = "/dxf-library";

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

export interface DxfFile {
  content_item_id: string;
  filename: string;
  /** Human-curated tag — defaults to ``null``. PATCH /files/{id} updates. */
  category: string | null;
  /** Free-form note attached to the file. */
  description?: string | null;
  /** Ingestion pipeline status: ``pending`` / ``processing`` / ``completed`` / ``failed``. */
  status: string;
  file_size: number;
  /** Presigned URL for download (BE refreshes per-request). May be null on failed rows. */
  download_url?: string | null;
  /** Presigned URL for the rendered preview (BE auto-renders DXF/DWG to PNG). */
  preview_url?: string | null;
  created_at: string;
  updated_at: string;
  /** Format discriminator surfaced from ``content_items.cad_analysis`` —
   *  drives the library badge + subtab bucketing + viewer dispatch.
   *  ``null`` when the analyzer hasn't run yet.
   *
   *  Mechanical axis: ``dxf`` / ``dwg`` (2D) + ``step`` / ``iges`` / ``stl``
   *  (3D). Fashion axis (FashionSigLIP garment pipeline): ``fashion-dxf``
   *  (2D apparel pattern) + ``fashion-3d`` (3D garment mesh — OBJ/glTF/GLB/
   *  FBX embedded on the fashion axis, previewed via a rendered contact-
   *  sheet montage PNG) + ``fashion-project`` (proprietary CLO/Marvelous
   *  ``.zprj`` / ``.zpac`` container, best-effort thumbnail). */
  cad_format?:
    | "dxf"
    | "dwg"
    | "step"
    | "iges"
    | "stl"
    | "fashion-dxf"
    | "fashion-3d"
    | "fashion-project"
    | null;
  /** Number of cylindrical holes detected by the topology analyzer
   *  (3D formats only — 0 for DXF + STL). Searchable facet — drives
   *  the row chip "🕳 4". */
  holes_count?: number | null;
  /** ``"catia_v4" | "solidworks" | "default"`` — vendor hint extracted
   *  from the file header. Drives the healing tolerance preset on the
   *  BE; surfaced as a provenance badge in the detail panel. */
  source_hint?: string | null;
  /** Total volume in mm³ — populated only for 3D solid models
   *  (STEP/IGES). ``null`` for DXF (2D) and STL (mesh — unreliable). */
  volume_mm3?: number | null;
  /** Bounding box in mm, JSONB shape ``{xmin, ymin, zmin, xmax, ymax,
   *  zmax, dx, dy, dz}`` from the 3D analyzer. ``null`` for DXF. */
  bbox_mm?: Record<string, number> | null;
  // BE returns extra columns (version count, cad_analysis, etc.) — accessible
  // via the loose index signature so feature code can opt-in without forcing
  // updates here when the BE grows.
  [k: string]: unknown;
}

export interface DxfFileListResponse {
  files: DxfFile[];
  total: number;
}

export interface ListDxfFilesParams {
  category?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface DxfCategory {
  name: string;
  file_count: number;
}

export interface DxfCategoryListResponse {
  categories: DxfCategory[];
}

export interface UpdateDxfMetadataRequest {
  category?: string | null;
  description?: string | null;
}

export interface DxfFileVersion {
  readonly id: string;
  readonly version_number: number;
  readonly created_at: string;
  readonly created_by?: string | null;
  readonly modification_type?: string | null;
  readonly modification_summary?: string | null;
  readonly minio_object_key?: string | null;
  readonly file_size?: number | null;
  readonly download_url?: string | null;
  readonly [k: string]: unknown;
}

export interface DxfFileVersionsResponse {
  readonly versions: readonly DxfFileVersion[];
}

// ---------------------------------------------------------------------------
// Similar files — multi-axis nearest-neighbor search
// ---------------------------------------------------------------------------

/**
 * Orthogonal similarity axes surfaced by ``GET /dxf-library/files/{id}/similar``.
 *
 *   - ``shape``     — 3D geometry vector (Shape DNA / topology hash).
 *   - ``physical``  — convex-hull invariant (format-invariant; Jun 2026).
 *   - ``multiview`` — 1024-dim mean-pooled image embedding across 7
 *                      canonical viewpoints (SOTA Duoduo CLIP; Jun 2026).
 *   - ``visual``    — multimodal image embedding of the rendered preview
 *                      (single isometric render; Jun 2026 — surfaces
 *                      cosmetically-similar parts that diverge on geometry).
 *   - ``semantic``  — LLM-derived description / category embedding.
 *   - ``combined``  — Reciprocal Rank Fusion across the 3 geometric axes.
 *   - ``features``  — hole/feature pattern signature (legacy).
 */
export type SimilarCadMetric =
  | "shape"
  | "physical"
  | "multiview"
  | "visual"
  | "semantic"
  | "combined"
  | "features";

/** Per-match row in a similarity response. ``similarity`` is the
 *  bi-encoder cosine to the query part (0..1, 1 = identical
 *  fingerprint). ``visual_score`` is the Gemini Vision VLM-judge
 *  re-ranking score (0..1), populated only for image-based metrics
 *  (``multiview`` / ``visual`` / ``combined``) where the BE runs the
 *  cross-encoder pass — ``null`` for the geometry-only axes. */
export interface SimilarFileEntry {
  readonly content_item_id: string;
  readonly filename: string;
  readonly format: "dxf" | "dwg" | "step" | "iges" | "stl";
  readonly similarity: number;
  readonly volume_mm3?: number | null;
  readonly holes_count?: number | null;
  readonly drawing_number?: string | null;
  /** VLM caption (Gemini Vision) persisted at ingest time —
   *  "rectangular pull handle or mounting bracket" style. */
  readonly description?: string | null;
  /** Cross-encoder VLM-judge score (0..1). Only present when the
   *  metric supports visual rerank (multiview / visual / combined). */
  readonly visual_score?: number | null;
  /** Italian one-line VLM justification — useful as tooltip text. */
  readonly visual_reason?: string | null;
  /** Presigned MinIO URL for the cached iso-view PNG (1h TTL). The
   *  FE uses this as the thumbnail in the grid; ``null`` when the
   *  iso-view backfill hasn't run for this model yet. */
  readonly preview_url?: string | null;
}

/** Same-stem same-part neighbour — handled separately from the
 *  ranking grid because it's not a "similar" candidate but a
 *  different-format / different-revision export of the SAME logical
 *  CAD model. Surfaced by the BE's R-CAD-1 stem-match rule. */
export interface SimilarCadTwin {
  readonly content_item_id: string;
  readonly filename: string;
  readonly format: "dxf" | "dwg" | "step" | "iges" | "stl";
  readonly similarity: number;
  readonly description?: string | null;
  readonly preview_url?: string | null;
}

export interface SimilarFilesResponse {
  readonly target_content_item_id: string;
  readonly metric: SimilarCadMetric;
  /** Filename of the query part — echoed back so the FE doesn't need
   *  a second lookup just to render the header. */
  readonly query_filename?: string | null;
  /** VLM caption of the query part — for the "Cos'è questo pezzo?"
   *  card. */
  readonly query_description?: string | null;
  /** Whether the BE ran the Gemini Vision VLM-judge rerank pass.
   *  When ``true`` the FE can decorate the panel with a "verified
   *  by Gemini Vision" badge. */
  readonly visual_rerank_applied?: boolean;
  /** Twins (same part, different format / revision) surfaced from
   *  R-CAD-1 stem-match. Excluded from ``results`` so the FE can
   *  render them in a dedicated "Stesso pezzo, formato diverso"
   *  block. */
  readonly twins?: readonly SimilarCadTwin[];
  readonly results: readonly SimilarFileEntry[];
}

export interface ListSimilarCadFilesParams {
  /** Defaults BE-side (10). Cap at ~30 to keep the panel scannable. */
  readonly limit?: number;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

function buildQuery(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (entries.length === 0) return "";
  const search = new URLSearchParams();
  for (const [k, v] of entries) {
    search.append(k, String(v));
  }
  return `?${search.toString()}`;
}

export async function listDxfFiles(params: ListDxfFilesParams = {}): Promise<DxfFileListResponse> {
  return apiClient.get<DxfFileListResponse>(
    `${BASE}/files${buildQuery(params as Record<string, unknown>)}`,
  );
}

export async function getDxfFile(contentItemId: string): Promise<DxfFile> {
  return apiClient.get<DxfFile>(`${BASE}/files/${encodeURIComponent(contentItemId)}`);
}

export async function exploreDxfFile(contentItemId: string): Promise<Record<string, unknown>> {
  return apiClient.get<Record<string, unknown>>(
    `${BASE}/files/${encodeURIComponent(contentItemId)}/explore`,
  );
}

export async function listDxfCategories(): Promise<DxfCategoryListResponse> {
  return apiClient.get<DxfCategoryListResponse>(`${BASE}/categories`);
}

/**
 * BE: ``GET /dxf-library/files/{id}/versions``. Returns the modification
 * history (newest first) — used by the Tecnico right-pane "Revisioni di
 * questo disegno" list.
 */
export async function listDxfFileVersions(contentItemId: string): Promise<DxfFileVersionsResponse> {
  return apiClient.get<DxfFileVersionsResponse>(
    `${BASE}/files/${encodeURIComponent(contentItemId)}/versions`,
  );
}

/**
 * BE: ``GET /dxf-library/files/{id}/similar?metric=<axis>``. Returns the
 * k nearest neighbours along the chosen orthogonal axis (see
 * :type:`SimilarCadMetric`). The BE owns the ANN index; the FE just
 * picks the axis and renders the result.
 */
export async function listSimilarCadFiles(
  contentItemId: string,
  metric: SimilarCadMetric,
  params: ListSimilarCadFilesParams = {},
): Promise<SimilarFilesResponse> {
  const query = buildQuery({ metric, ...(params as Record<string, unknown>) });
  return apiClient.get<SimilarFilesResponse>(
    `${BASE}/files/${encodeURIComponent(contentItemId)}/similar${query}`,
  );
}

/**
 * BE: ``POST /cad-library/search-by-file`` (multipart, field ``file``).
 * Live *query-by-file* similarity — the user uploads a technical file that is
 * NOT in the archive and we find the nearest parts by embedding it on the fly:
 *
 *   - STEP / IGES / STL → multi-view 3D axis (SOTA for 3D↔3D similarity)
 *   - image (PNG/JPG) / PDF → visual image axis (cross-format retrieval)
 *
 * Ephemeral: the uploaded file is embedded live and NOT ingested or persisted.
 * Returns the same {@link SimilarFilesResponse} shape as the stored-model
 * ``/similar`` endpoint so the FE reuses one rendering path.
 */
export async function searchSimilarCadByFile(
  file: File,
  topK?: number,
): Promise<SimilarFilesResponse> {
  const form = new FormData();
  form.append("file", file, file.name);
  const query = buildQuery({ top_k: topK });
  return apiClient.postMultipart<SimilarFilesResponse>(`/cad-library/search-by-file${query}`, form);
}

// ---------------------------------------------------------------------------
// BOM Excel export (PR-A7)
// ---------------------------------------------------------------------------

/**
 * Result of a BOM Excel export — the xlsx blob plus the server-suggested
 * filename (lifted from the ``Content-Disposition`` header, RFC 6266).
 */
export interface BomExportResult {
  readonly blob: Blob;
  readonly filename: string;
}

/**
 * Trigger the server-side BOM Excel generator and return the resulting
 * xlsx as a blob.
 *
 * BE: ``GET /dxf-library/files/{id}/export/bom`` (streams an
 * ``application/vnd.openxmlformats-officedocument.spreadsheetml.sheet``
 * with one sheet per block-type plus a "Riepilogo" summary). The endpoint
 * fails with 422 when the drawing has no block components yet — callers
 * should surface a "Niente da esportare" toast in that case.
 *
 * We extract the suggested filename from ``Content-Disposition`` so the
 * caller can pass it straight into an ``<a download="...">`` anchor.
 * The RFC 6266 ``filename*=UTF-8''`` form wins over the ASCII fallback.
 */
export async function exportDxfBom(contentItemId: string): Promise<BomExportResult> {
  const { blob, headers } = await apiClient.getBlobWithHeaders(
    `${BASE}/files/${encodeURIComponent(contentItemId)}/export/bom`,
  );
  const disposition = headers.get("Content-Disposition") ?? "";
  const rfcMatch = disposition.match(/filename\*=UTF-8''([^\s;]+)/i);
  const asciiMatch = disposition.match(/filename="?([^";\r\n]+)"?/i);
  const filename = rfcMatch
    ? decodeURIComponent(rfcMatch[1] ?? "")
    : (asciiMatch?.[1]?.trim() ?? "BOM.xlsx");
  return { blob, filename };
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export async function updateDxfMetadata(
  contentItemId: string,
  body: UpdateDxfMetadataRequest,
): Promise<{ success: boolean }> {
  return apiClient.patch<{ success: boolean }>(
    `${BASE}/files/${encodeURIComponent(contentItemId)}`,
    body,
  );
}

export async function deleteDxfFile(contentItemId: string): Promise<{ success: boolean }> {
  return apiClient.delete<{ success: boolean }>(
    `${BASE}/files/${encodeURIComponent(contentItemId)}`,
  );
}

export async function renameDxfCategory(
  oldName: string,
  newName: string,
): Promise<{ success: boolean; updated: number }> {
  return apiClient.put<{ success: boolean; updated: number }>(`${BASE}/categories`, {
    old_name: oldName,
    new_name: newName,
  });
}
